import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const UserDashboard = () => {
  const [userData, setUserData] = useState(null); // Store user data
  const [notifications, setNotifications] = useState([]); // Store notifications
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch user data when the component loads
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.eatourshorts.com'}/user-dashboard`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setUserData(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch user data.');
      }
    };

    // Fetch notifications when the component loads
    const fetchNotifications = async () => {
      try {
        const userId = localStorage.getItem('user_id');
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.eatourshorts.com'}/notifications/${userId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setNotifications(response.data);
      } catch (err) {
        console.error('Error fetching notifications:', err);
      }
    };

    fetchUserData();
    fetchNotifications();
  }, []);

// Check if there are unread notifications
const hasUnreadNotifications = notifications.some((notification) => !notification.is_read);

if (!userData) {
  return <p>Loading...</p>;
}

  const toggleVisibility = async (filmId, currentVisibility) => {
    try {
      const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
  
      // Check if the user already has 5 public films before making a film public
      if (newVisibility === 'public') {
        const publicFilmsCount = userData.films.filter((film) => film.visibility === 'public').length;
        if (publicFilmsCount >= 5) {
          alert('You can only have up to 5 public films at any point in time.');
          return;
        }
      }
  
      await axios.patch(
        `${process.env.REACT_APP_API_URL || 'https://api.eatourshorts.com'}/films/${filmId}/visibility`,
        { visibility: newVisibility },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
  
      // Re-fetch user data to ensure the local state is updated
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.eatourshorts.com'}/user-dashboard`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setUserData(response.data); // Update the local state with the latest data
    } catch (err) {
      console.error('Error updating film visibility:', err.response?.data || err);
      alert(err.response?.data?.error || 'Failed to update film visibility. Please try again.');
    }
  };

  if (!userData) {
    return <p>Loading...</p>;
  }

  // Sort films alphabetically by title
  const sortedFilms = [...userData.films].sort((a, b) =>
    a.title.localeCompare(b.title)
  );

  return (
  <div
    style={{
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      color: '#333',
      lineHeight: '1.6',
      background: 'linear-gradient(to bottom, #fff4e6, #ffe8cc)',
      minHeight: '100vh',
    }}
  >
    <h1
      style={{
        color: '#2e8b57',
        marginBottom: '20px',
        textAlign: 'center',
        fontSize: '2.5rem',
      }}
    >
      Welcome, {userData.username}!
    </h1>

    {/* Flex container for info and actions */}
    <div style={{
      display: 'flex',
      gap: '40px',
      alignItems: 'flex-start',
      marginBottom: '30px',
      justifyContent: 'center',
      flexWrap: 'wrap',
    }}>
      {/* Left column: User Info */}
      <div
        style={{
          backgroundColor: '#ffffff',
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          minWidth: '280px',
          maxWidth: '600px',
          flex: '1 1 320px',
          fontSize: '1.45rem',
          minWidth: 0, // allow shrinking
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <p>
          <strong>Email:</strong> {userData.email}
        </p>
        <p>
        <strong>Tokens:</strong> {userData.tokens}
      </p>
<p>
  <strong>Average Rating:</strong>
  <span style={{ color: '#FFD700', fontSize: '1.5rem', lineHeight: '1', verticalAlign: 'middle' }}>
    {Array.from({ length: 5 }, (_, index) => {
      const decimalValue = userData.average_rating - index;
      if (decimalValue >= 1) {
        return (
          <span
            key={index}
            style={{ verticalAlign: 'middle', display: 'inline-block', width: '1em', height: '1em', lineHeight: '1' }}
          >
            &#9733;
          </span>
        );
      } else if (decimalValue > 0) {
        return (
          <span
            key={index}
            style={{
              position: 'relative',
              display: 'inline-block',
              width: '1em',
              height: '1em',
              verticalAlign: 'middle',
              lineHeight: '1',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: `${decimalValue * 100}%`,
                height: '100%',
                color: '#FFD700',
                overflow: 'hidden',
                fontSize: '1em',
                lineHeight: '1',
              }}
            >
              &#9733;
            </span>
            <span style={{ color: '#ddd', fontSize: '1em', lineHeight: '1' }}>&#9733;</span>
          </span>
        );
      } else {
        return (
          <span
            key={index}
            style={{ color: '#ddd', verticalAlign: 'middle', display: 'inline-block', width: '1em', height: '1em', lineHeight: '1' }}
          >
            &#9733;
          </span>
        );
      }
    })}
  </span>
  <span style={{ color: '#555', fontSize: '1.1rem', marginLeft: '8px', verticalAlign: 'middle' }}>
    ({userData.average_rating?.toFixed(2)})
  </span>
</p>
        <p>
          <strong>Total Feedbacks:</strong> {userData.total_reviews}
        </p>
        <button
          onClick={() => navigate('/notifications')}
          style={{
            backgroundColor: hasUnreadNotifications ? '#ff0000' : '#007bff',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '10px',
          }}
        >
          {hasUnreadNotifications ? 'View Notifications (Unread)' : 'View Notifications'}
        </button>
      </div>

      {/* Right column: Actions */}
      <div
          style={{
            backgroundColor: '#ffffff',
            padding: '20px',
            borderRadius: '10px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            minWidth: '280px',
            maxWidth: '600px',
            flex: '1 1 320px',
            marginTop: 0,            
            minWidth: 0,
            width: '100%',
            boxSizing: 'border-box',
        }}
      >
        <h2 style={{ color: '#333', fontSize: '1.8rem', textAlign:'center', marginTop: '15px', marginBottom: '15px' }}>
          Actions
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button
            onClick={() => navigate('/upload-film')}
            style={{
              padding: '10px 15px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              fontSize: '1.5rem',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = '#0056b3')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = '#007bff')}
          >
            Upload a New Film
          </button>
          <button
            onClick={() => navigate('/change-password')}
            style={{
              padding: '10px 15px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              fontSize: '1.5rem',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = '#0056b3')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = '#007bff')}
          >
            Change Password
          </button>
          <button
            onClick={() => navigate('/my-reviews')}
            style={{
              padding: '10px 15px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              fontSize: '1.5rem',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = '#0056b3')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = '#007bff')}
          >
            View or Edit my Feedbacks
          </button>
          <button
            onClick={() => navigate('/buy-tokens')}
            style={{
              padding: '10px 15px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              fontSize: '1.5rem',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = '#218838')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = '#28a745')}
          >
            Buy Tokens
          </button>
          <button
            onClick={() => navigate('/transaction-history')}
            style={{
              padding: '10px 15px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              fontSize: '1.5rem',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = '#5a6268')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = '#6c757d')}
          >
            View Transaction History
          </button>
        </div>
      </div>
    </div>
  
      <h2 style={{ marginTop: '30px', color: '#333', fontSize: '1.8rem' }}>
        Your Films (3 public max)
      </h2>
      {sortedFilms.length > 0 ? (
        <ul
           style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '20px',
            padding: '0',
            listStyle: 'none',
            marginTop: '20px',
            width: '100%', // ensures it doesn't overflow
            boxSizing: 'border-box',
          }}
        >
          {sortedFilms.map((film) => (
            <li
              key={film.id}
              style={{
                padding: '15px',
                border: '1px solid #ccc',
                borderRadius: '10px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: film.visibility === 'public' ? '#e6ffe6' : '#f9f9f9', // Soft green for public, soft gray for private
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <strong style={{ fontSize: '18px', color: '#333' }}>{film.title}</strong>
              <span
                style={{
                  marginLeft: '10px',
                  fontWeight: 'bold',
                  color: film.visibility === 'public' ? 'green' : 'red',
                }}
              >
                {film.visibility === 'public' ? '🌐 Public' : '🔒 Private'}
              </span>
              <p style={{ margin: '10px 0' }}>
                <strong>Genre:</strong> {film.genre} | <strong>Length:</strong>{' '}
                {film.length} minutes
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => navigate(`/films/${film.id}`)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = '#0056b3')}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = '#007bff')}
                >
                  View Details
                </button>
                <button
                  onClick={() => toggleVisibility(film.id, film.visibility)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: film.visibility === 'public' ? 'green' : 'gray',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor =
                      film.visibility === 'public' ? '#218838' : '#505050')
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor =
                      film.visibility === 'public' ? 'green' : 'gray')
                  }
                >
                  {film.visibility === 'public' ? 'Public' : 'Private'}
                </button>
                <button
                  onClick={() =>
                    navigate(`/films/${film.id}/edit-feedback-categories`)
                  }
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#ffc107',
                    color: '#333',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = '#e0a800')}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = '#ffc107')}
                >
                  Adjust Feedback Categories and Notes
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p style={{ marginTop: '20px', color: '#666' }}>
          You haven't uploaded any films yet.
        </p>
      )}
      
    </div>
  );
};

export default UserDashboard;